import { Input, OdinIcon, OdinIconSize } from '@myosh/odin-components';
import { t } from 'i18next';
import { useMemo, useRef, useState } from 'react';
import ActionIcon from '../action-icon';
import type { ViewNameOption } from './view-dropdown';

interface ViewNameEditorProps {
  view: ViewNameOption;
  isEven: boolean;
  onRemove: (view: string) => void;
  onNameUpdate: (newName: string) => Promise<string | void>;
}

function ViewNameEditor({ view, isEven, onRemove, onNameUpdate }: ViewNameEditorProps) {
  const viewName = useMemo(() => view.text ?? '', [view.text]);
  const viewNameInputRef = useRef<string>(viewName);
  const [editMode, setEditMode] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const enableEditMode = () => setEditMode(true);
  const disableEditMode = () => {
    setHasError(false);
    setEditMode(false);
  };

  const handleViewNameChange = (value?: string) => {
    if (value!.trim().length === 0) {
      setHasError(true);
      return;
    }
    setHasError(false);
    viewNameInputRef.current = value || '';
  };

  const removeItem = () => setShowDeleteConfirmation(true);
  const confirmRemoveItem = () => onRemove(view.value);
  const cancelRemoveItem = () => setShowDeleteConfirmation(false);

  const applyChanges = async () => {
    if (viewNameInputRef.current === viewName) {
      setEditMode(false);
      return;
    }

    if (viewNameInputRef.current.trim().length > 0) {
      setEditMode(false);
      setIsLoading(true);
      await onNameUpdate(viewNameInputRef.current).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const inputStyles = useMemo(() => {
    let classes = 'view-name__input';
    if (hasError) {
      classes += ' input-error';
    }

    return classes;
  }, [hasError]);

  return (
    <div className="view-name-editor__wrapper" data-even={isEven}>
      <OdinIcon
        icon="Globe"
        size={OdinIconSize.ExtraSmall}
        className={`mr-2 text-primary-1 ${view.privateView ? 'invisible' : 'visible'}`}
      />
      {view?.defaultView ? (
        <p className="view-name-editor" title={viewName}>
          {viewName}
        </p>
      ) : (
        <>
          {showDeleteConfirmation ? (
            <div className="delete-confirmation">
              <div className="delete-confirmation__text">
                <span>{String(t('delete'))}</span>
                <span className="delete-confirmation__title" title={viewName}>{`"${viewName}"`}</span>
                <span>?</span>
              </div>
              <ActionIcon icon="Check" onClick={confirmRemoveItem} iconStyles="text-mono-1 hover:text-mono-1" />
              <ActionIcon icon="X" onClick={cancelRemoveItem} iconStyles="text-mono-1 hover:text-mono-1" />
            </div>
          ) : (
            <>
              <div className="view-name-editor">
                {editMode && !isLoading ? (
                  <Input value={viewName} onChange={handleViewNameChange} inputStyles={inputStyles} name={''} />
                ) : isLoading ? (
                  <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />
                ) : (
                  <p title={viewName}>{viewName}</p>
                )}
                <div className="view-name-editor__actions">
                  {editMode ? (
                    <>
                      <ActionIcon icon="Check" onClick={applyChanges} />
                      <ActionIcon icon="X" onClick={disableEditMode} />
                    </>
                  ) : (
                    <>
                      <ActionIcon icon="Pencil" onClick={enableEditMode} />
                      <ActionIcon icon="Trash" onClick={removeItem} />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ViewNameEditor;
